import { useState, useRef, useEffect } from "react"
import toast from "react-hot-toast"
import { lumi } from "../lib/lumi"
import type { EquipmentIssue, IssueAttachment } from "../hooks/useEquipmentIssues"

interface Props {
  issue: EquipmentIssue
  onClose: () => void
  onSubmit: (data: {
    resolutionContent: string
    resolutionAttachments: IssueAttachment[]
    resolvedByName: string
  }) => Promise<void>
  resolverName: string
}

function isImageFile(name: string) {
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
}

export function EquipmentResolutionModal({ issue, onClose, onSubmit, resolverName }: Props) {
  const [content, setContent] = useState(issue.resolutionContent || "")
  const [attachments, setAttachments] = useState<IssueAttachment[]>(
    issue.resolutionAttachments || []
  )
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving && !uploading) {
        onClose()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [saving, uploading, onClose])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    if (attachments.length + files.length > 8) {
      toast.error("결과 사진/문서는 최대 8개까지 첨부할 수 있습니다.")
      return
    }
    setUploading(true)
    try {
      const uploaded = await lumi.tools.file.upload(files)
      const arr = Array.isArray(uploaded) ? uploaded : [uploaded]
      const next: IssueAttachment[] = arr.map((f: any) => {
        const name = f.fileName || f.name || "첨부파일"
        return {
          url: f.fileUrl || f.url,
          name,
          type: isImageFile(name) ? "image" : "file",
        }
      })
      setAttachments((prev) => [...prev, ...next])
    } catch (error) {
      console.error("결과 첨부 업로드 실패:", error)
      toast.error("파일 업로드 중 오류가 발생했습니다.")
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ""
    }
  }

  const removeAttachment = (url: string) => {
    setAttachments((prev) => prev.filter((a) => a.url !== url))
  }

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error("조치 결과 내용을 입력해 주세요.")
      return
    }
    setSaving(true)
    try {
      await onSubmit({
        resolutionContent: content.trim(),
        resolutionAttachments: attachments,
        resolvedByName: resolverName,
      })
      toast.success("조치 결과가 저장되었습니다.")
      onClose()
    } catch (error) {
      console.error("조치 결과 저장 실패:", error)
      toast.error("저장 중 오류가 발생했습니다.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <h3 className="text-base font-bold text-gray-900">조치 결과 등록</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {/* 원 이슈 요약 (비교 맥락 제공) */}
          <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2">
            <p className="text-[11px] font-semibold text-red-500">이슈 발생 내용</p>
            <p className="text-sm font-medium text-gray-800 mt-0.5">
              {issue.title?.trim() || "제목없음"}
            </p>
            <p className="text-xs text-gray-600 whitespace-pre-line mt-1 min-h-[6.25rem] max-h-40 overflow-y-auto leading-5 pr-1">
              {issue.issueContent}
            </p>
          </div>

          {/* 조치 결과 내용 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              조치 결과 내용 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              placeholder="어떤 조치를 어떻게 완료했는지 입력하세요."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-y min-h-[7rem]"
            />
          </div>

          {/* 결과 사진/문서 */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold text-gray-700">
                조치 결과 사진/문서
              </label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md hover:bg-emerald-100 disabled:opacity-50"
              >
                {uploading ? (
                  <div className="w-3.5 h-3.5 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
                파일 추가
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.hwp,.zip"
              onChange={handleFileSelect}
              className="hidden"
            />
            {attachments.length === 0 ? (
              <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
                결과를 증명할 사진이나 문서를 첨부하세요. (최대 8개)
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {attachments.map((a) =>
                  a.type === "image" ? (
                    <div key={a.url} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                      <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.url)}
                        className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/50 text-white text-xs flex items-center justify-center hover:bg-black/70"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div
                      key={a.url}
                      className="flex items-center gap-1.5 max-w-[180px] px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50"
                    >
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-[11px] text-gray-600 truncate">{a.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.url)}
                        className="text-red-400 hover:text-red-600 shrink-0"
                      >
                        ✕
                      </button>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || uploading}
            className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? "저장 중..." : "결과 저장"}
          </button>
        </div>
      </div>
    </div>
  )
}
